import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Copy, Search } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from './shared';

/**
 * Modal launched from the Invoice form's "Clone" action. Lets the admin
 * choose whether to keep the same customer or retarget the clone to a
 * different existing customer. Hits POST /api/billing/invoices/{id}/clone
 * and navigates to the new invoice on success.
 */
const CloneInvoiceModal = ({ invoice, sourceCustomer, onClose, onCloned }) => {
  const { authHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState('same'); // 'same' | 'different'
  const [targetId, setTargetId] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/customers`, { headers: authHeader() });
        if (!cancelled) setCustomers(data || []);
      } catch (e) {
        if (!cancelled) setErr(formatErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = customers.filter((c) => c.id !== invoice?.customer_id);
    if (!q) return list.slice(0, 50);
    return list.filter((c) => {
      const hay = [c.prefix, c.name, c.phone, c.email].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 50);
  }, [customers, query, invoice?.customer_id]);

  const submit = async () => {
    setErr('');
    if (mode === 'different' && !targetId) {
      setErr('Please pick a target customer or switch to "Same customer".');
      return;
    }
    setSaving(true);
    try {
      const body = mode === 'different' ? { customer_id: targetId } : {};
      const { data } = await axios.post(
        `${API}/billing/invoices/${invoice.id}/clone`,
        body,
        { headers: authHeader() },
      );
      onCloned?.(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="clone-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Copy size={16} className="text-slate-700" />
            <h2 className="text-sm font-semibold text-slate-900">Clone Invoice</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="clone-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-slate-500">
            Duplicate <b>{invoice?.number}</b> as a fresh draft invoice. Line items, subject, notes
            and bank/T&amp;C overrides are carried over. Payments, status, due date and warranty
            link are reset.
          </p>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="clone-error">
              {err}
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="clone-mode-same">
              <input
                type="radio"
                name="clone-mode"
                value="same"
                checked={mode === 'same'}
                onChange={() => setMode('same')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">Same customer</div>
                <div className="text-xs text-slate-500">
                  Keep on <b>{sourceCustomer?.name || '—'}</b>. Useful for repeat services or recurring jobs.
                </div>
              </div>
            </label>

            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="clone-mode-different">
              <input
                type="radio"
                name="clone-mode"
                value="different"
                checked={mode === 'different'}
                onChange={() => setMode('different')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">Different customer</div>
                <div className="text-xs text-slate-500">
                  Re-assign the cloned invoice to another existing customer.
                </div>
              </div>
            </label>
          </div>

          {mode === 'different' && (
            <div className="space-y-2 border-t border-slate-200 pt-3">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search customers by name, phone or email…"
                  className={`${inputCls} pl-8`}
                  data-testid="clone-customer-search"
                />
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {loading && (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="animate-spin" size={14} /> Loading customers…
                  </div>
                )}
                {!loading && filtered.length === 0 && (
                  <div className="p-3 text-xs text-slate-500">No matching customers.</div>
                )}
                {!loading && filtered.map((c) => {
                  const active = targetId === c.id;
                  const display = [c.prefix, c.name].filter(Boolean).join(' ');
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setTargetId(c.id)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-teal-50 ${active ? 'bg-teal-50' : ''}`}
                      data-testid={`clone-pick-${c.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{display}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {c.phone || '—'}{c.email ? ` · ${c.email}` : ''}
                        </div>
                      </div>
                      {active && <span className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold">selected</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200"
            data-testid="clone-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || (mode === 'different' && !targetId)}
            className="inline-flex items-center gap-1.5 text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg"
            data-testid="clone-submit-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
            {saving ? 'Cloning…' : 'Create Clone'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloneInvoiceModal;
